import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useCompanies } from '../hooks/useCompanies';
import { useCompanyStatus } from '../hooks/useCompanyStatus';
import { CompanyList } from '../components/CompanyList';
import { CreateCompanyDialog } from '../components/CreateCompanyDialog';
import { EditCompanyDialog } from '../components/EditCompanyDialog';
import { companyTableColumns } from '../config/companyTableColumns';
import { useImpersonation } from '@/features/auth/hooks/useImpersonation';
import type { Company } from '../types/company.types';

interface CompaniesPageProps {
  isSuperAdmin?: boolean | undefined;
}

export function CompaniesPage({ isSuperAdmin = false }: CompaniesPageProps): React.JSX.Element {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const { companies, hasData, isLoading, isError } = useCompanies();
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    toggleSort,
    activeFilterCount,
  } = useTableFilters(companies, companyTableColumns);
  const { activate, suspend } = useCompanyStatus();
  const { startImpersonation } = useImpersonation();

  const handleEdit = (company: Company): void => {
    setEditingCompany(company);
  };

  const handleCreate = (): void => {
    setIsCreateDialogOpen(true);
  };

  const handleEnterCompany = (company: Company): void => {
    const session = {
      id: `imp-${Date.now()}`,
      impersonatorId: '',
      targetCompanyId: company.id,
      targetCompanyName: company.name,
      sessionToken: `token-${company.id}`,
      startTime: new Date().toISOString(),
      isActive: true,
      durationInMinutes: 60,
      createdAt: new Date().toISOString(),
    };

    const context = {
      companyId: company.id,
      companyName: company.name,
      companyStatus: company.status,
      effectiveRoles: ['ADMIN' as const, 'MANAGER' as const],
      sessionStartTime: new Date().toISOString(),
      sessionToken: `token-${company.id}`,
    };

    startImpersonation(session, context);
    navigate(`/companies/${company.id}/dashboard`);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Companies</h1>
          {!isLoading && !(isError && !hasData) && <ResultCount count={filteredSorted.length} />}
        </div>
        {isSuperAdmin && <Button onClick={handleCreate}>Create Company</Button>}
      </div>

      <DataTableFilters
        columns={companyTableColumns}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {isError && (
        <div className="text-center py-8 text-destructive">
          An error occurred while loading companies
        </div>
      )}

      <CompanyList
        companies={filteredSorted}
        isLoading={isLoading}
        sort={sort}
        onToggleSort={toggleSort}
        onEdit={handleEdit}
        onActivate={isSuperAdmin ? activate : undefined}
        onSuspend={isSuperAdmin ? suspend : undefined}
        onEnterCompany={isSuperAdmin ? handleEnterCompany : undefined}
        showStatusActions={isSuperAdmin}
      />

      <CreateCompanyDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

      {editingCompany && (
        <EditCompanyDialog
          open={!!editingCompany}
          onOpenChange={(open) => !open && setEditingCompany(null)}
          company={editingCompany}
        />
      )}
    </div>
  );
}
